import { invariantResponse } from '@epic-web/invariant'
import { json, type LoaderFunctionArgs } from '@remix-run/node'
import { NavLink, Outlet, useLoaderData } from '@remix-run/react'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { cn } from '#app/utils/misc.tsx'
import { useOptionalUser } from '#app/utils/user.ts'

export async function loader({ params }: LoaderFunctionArgs) {
	const owner = await prisma.user.findFirst({
		select: {
			id: true,
			name: true,
			username: true,
			image: { select: { id: true } },
			notes: { select: { id: true, title: true } },
		},
		where: { username: params.username },
	})

	invariantResponse(owner, 'Owner not found', { status: 404 })

	return json({ owner })
}

export default function NotesRoute() {
	const data = useLoaderData<typeof loader>()
	const user = useOptionalUser()
	const isOwner = user?.id === data.owner.id
	const navLinkDefaultClassName =
		'line-clamp-2 block rounded-l-full py-2 pl-8 pr-6 text-base lg:text-xl'
	return (
		<div className="grid w-full grid-cols-4 md:container md:pr-0">
			<div className="relative col-span-1">
				<div className="inset-0 flex flex-col">
					<ul className="list-none overflow-y-auto overflow-x-hidden p-0">
						{isOwner ? (
							<li className="p-1 pr-0">
								<NavLink
									to="new"
									className={({ isActive }) => cn(navLinkDefaultClassName)}
								>
									<Icon name="plus">New Note</Icon>
								</NavLink>
							</li>
						) : null}
						{data.owner.notes.map(note => (
							<li key={note.id} className="p-1 pr-0">
								<NavLink
									to={note.id}
									preventScrollReset
									prefetch="intent"
									className={({ isActive }) => cn(navLinkDefaultClassName)}
								>
									{note.title}
								</NavLink>
							</li>
						))}
					</ul>
				</div>
			</div>
			<div className="relative col-span-3">
				<Outlet />
			</div>
		</div>
	)
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: ({ params }) => (
					<p>No user with the username "{params.username}" exists</p>
				),
			}}
		/>
	)
}
